import { useSelector } from "@legendapp/state/react";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import { EvaluationCriteria } from "./evaluation-criteria";
import { HowItWorks } from "./how-it-works";
import { ResultsPanel } from "./results-panel";
import { ResultsSection } from "./results-section";
import {
  setThumbnailReviewerState,
  STEP,
  ThumbnailReviewerState,
  thumbnailReviewerState,
  getThumbnailReviewerState,
  MAX_USAGE_DAILY,
  checkUsageLimit,
  udpateUsageCount,
  getImageScores,
} from "./state";
import { UploadSection } from "./upload-section";
import { UsageLimitModal } from "./usage-limit-modal";
import CaseStudiesSection from "./case-studies";
import BeforeAfterStudiesSection from "./before-after-studies";
import { ArrowRight, Loader2 } from "lucide-react";

export default function ThumbnailReviewer() {
  const { t } = useTranslation();
  const remainingUsageCount = useSelector(getThumbnailReviewerState.remainingUsageCount);

  // Add ref for the upload section
  const uploadSectionRef = useRef<HTMLDivElement>(null);
  // Add ref for the description section
  // const descriptionSectionRef = useRef<HTMLDivElement>(null);

  // Replace useState hooks with useSelector
  const step = useSelector(thumbnailReviewerState.step);
  const results = useSelector(thumbnailReviewerState.results);
  const image = useSelector(thumbnailReviewerState.image);
  const description = useSelector(thumbnailReviewerState.description);

  // Update state setters to use the observable
  const setStep = (newStep: ThumbnailReviewerState["step"]) => {
    setThumbnailReviewerState.step(newStep);
  };

  const resetReview = () => {
    setStep(STEP.UPLOAD);
    setThumbnailReviewerState.reset();
    udpateUsageCount(false);
  };

  // Determine text color based on usage count
  const getUsageTextColor = () => {
    if (remainingUsageCount <= 0) {
      return "text-red-500"; // Red for exceeded limit
    } else if (remainingUsageCount <= 1) {
      return "text-amber-500"; // Amber/orange for last try
    } else {
      return "text-green-500"; // Green for normal usage
    }
  };

  // Add loading state
  const [isLoading, setIsLoading] = useState(false);

  // Add effect to scroll to upload section when image is uploaded
  useEffect(() => {
    if (image && uploadSectionRef.current) {
      // Scroll the upload section into view with smooth behavior
      uploadSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [image]);

  // Add effect to scroll to description section when description is received
  // useEffect(() => {
  //   if (description && step === "description" && descriptionSectionRef.current) {
  //     // Scroll the description section into view with smooth behavior
  //     descriptionSectionRef.current.scrollIntoView({
  //       behavior: "smooth",
  //       block: "start",
  //     });
  //   }
  // }, [description, step]);

  useEffect(() => {
    udpateUsageCount(false);
  }, []);

  // Create a wrapper function for getImageScores to handle loading state
  const handleGetImageScores = async () => {
    setIsLoading(true);
    try {
      await getImageScores();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <HowItWorks />

      {/* Main Review Area with Side Panel */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Main Content Area */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <CardTitle>
                  {step === "upload"
                    ? t("thumbnailReviewer.upload.title")
                    : step === "description"
                      ? t("thumbnailReviewer.description.title")
                      : t("thumbnailReviewer.results.title")}
                </CardTitle>
                <CardDescription>
                  {step === "upload"
                    ? t("thumbnailReviewer.upload.description")
                    : step === "description"
                      ? t("thumbnailReviewer.description.description")
                      : t("thumbnailReviewer.results.description")}
                </CardDescription>
                <span className={`text-sm ${getUsageTextColor()}`}>
                  {t("thumbnailReviewer.usageLabel", {
                    current: MAX_USAGE_DAILY - remainingUsageCount,
                    max: MAX_USAGE_DAILY,
                  })}
                </span>
              </div>
              {step !== "upload" && (
                <Button variant="outline" onClick={resetReview}>
                  {t("thumbnailReviewer.results.actions.startNew")}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Add ref to the div containing UploadSection */}
            <div ref={uploadSectionRef}>
              <UploadSection />
            </div>

            {(step === STEP.UPLOAD || step === STEP.DESCRIPTION) && (
              <div className="mt-6 flex items-center justify-center">
                <Button
                  onClick={() => handleGetImageScores()}
                  className="gap-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("thumbnailReviewer.upload.analyzing")}
                    </>
                  ) : (
                    <>
                      {t("thumbnailReviewer.description.submit")} <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            )}

            {(step === "results" || step === "improvements") && results && (
              <div className="mt-6">
                <ResultsSection />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Side Panel */}
        <Card>
          <CardHeader>
            <CardTitle>
              {step === "results"
                ? t("thumbnailReviewer.results.title")
                : t("thumbnailReviewer.evaluationCriteria.title")}
            </CardTitle>
            <CardDescription>
              {step === "results"
                ? t("thumbnailReviewer.results.description")
                : t("thumbnailReviewer.evaluationCriteria.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === "results" || step === "improvements" ? (
              <ResultsPanel results={results!} />
            ) : (
              <EvaluationCriteria />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add the UsageLimitModal component */}
      <UsageLimitModal />

      {/* Add the Before/After Studies section */}
      <BeforeAfterStudiesSection />

      {/* Add the Case Studies section */}
      {/* <CaseStudiesSection /> */}
    </div>
  );
}
